import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Pagination,
  AppBar,
  Toolbar,
  Link,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PushPinIcon from '@mui/icons-material/PushPin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';

const TYPE_LABELS = {
  notice: '공지',
  free: '자유',
  qna: 'Q&A',
  tip: '팁',
  study: '스터디',
};

const TYPE_COLORS = {
  notice: 'error',
  free: 'primary',
  qna: 'secondary',
  tip: 'success',
  study: 'warning',
};

export default function CommunityPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ type: 'free', title: '', content: '', isPinned: false });
  const [editingId, setEditingId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsAdmin(payload.role === 'admin');
      } catch { /* ignore */ }
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, type: 'notice' };
      const res = await axios.get('/api/community/posts', { params });
      if (res.data.success) {
        setPosts(res.data.data.posts);
        setTotalPages(res.data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filterType]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleCreate = () => {
    setEditingId(null);
    setEditForm({ type: 'free', title: '', content: '', isPinned: false });
    setDialogOpen(true);
  };

  const handleEdit = (post) => {
    setEditingId(post.id);
    setEditForm({ type: post.type, title: post.title, content: post.content, isPinned: post.isPinned });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/community/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPosts();
      setSelectedPost(null);
    } catch (error) {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleSubmit = async () => {
    if (!editForm.title.trim() || !editForm.content.trim()) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingId) {
        await axios.put(`/api/community/posts/${editingId}`, editForm, { headers });
      } else {
        await axios.post('/api/community/posts', editForm, { headers });
      }
      setDialogOpen(false);
      fetchPosts();
    } catch (error) {
      alert('저장에 실패했습니다.');
    }
  };

  const handlePostClick = async (post) => {
    try {
      const res = await axios.get(`/api/community/posts/${post.id}`);
      if (res.data.success) {
        setSelectedPost(res.data.data);
      }
    } catch {
      setSelectedPost(post);
    }
  };

  const notices = posts.filter(p => p.type === 'notice');
  const otherPosts = posts.filter(p => p.type !== 'notice');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#1a1a1a' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link href="/#/" underline="none" sx={{ display: 'flex', alignItems: 'center' }}>
              <Box component="img" src="/logo.png" alt="KOAI" sx={{ height: 36 }} />
            </Link>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              커뮤니티
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Link href="/#/blog" underline="none" sx={{ color: 'white', fontSize: '0.9rem' }}>블로그</Link>
            <Link href="/#/" underline="none" sx={{ color: 'white', fontSize: '0.9rem' }}>홈</Link>
            {isAdmin ? (
              <Button size="small" variant="outlined" sx={{ color: '#aaa', borderColor: '#555', fontSize: '0.75rem' }} onClick={() => { localStorage.removeItem('token'); setIsAdmin(false); }}>로그아웃</Button>
            ) : (
              <Button size="small" variant="outlined" sx={{ color: '#aaa', borderColor: '#555', fontSize: '0.75rem' }} onClick={() => navigate('/')}>관리자 로그인</Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {selectedPost ? (
          /* Post Detail View */
          <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={() => setSelectedPost(null)} sx={{ mb: 2 }}>
              목록으로
            </Button>
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip label={TYPE_LABELS[selectedPost.type]} color={TYPE_COLORS[selectedPost.type]} size="small" />
                  {selectedPost.isPinned && <PushPinIcon fontSize="small" color="action" />}
                </Box>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {selectedPost.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
                  <Typography variant="body2">{selectedPost.authorName}</Typography>
                  <Typography variant="body2">{new Date(selectedPost.createdAt).toLocaleDateString('ko-KR')}</Typography>
                  <Typography variant="body2">조회 {selectedPost.viewCount}</Typography>
                </Box>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                  {selectedPost.content}
                </Typography>
                {isAdmin && (
                  <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(selectedPost)}>수정</Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(selectedPost.id)}>삭제</Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        ) : (
          /* List View */
          <Box>
            {/* Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>유형</InputLabel>
                <Select value={filterType} label="유형" onChange={(e) => { setFilterType(e.target.value); setPage(1); }}>
                  <MenuItem value="">전체</MenuItem>
                  <MenuItem value="notice">공지</MenuItem>
                  <MenuItem value="free">자유</MenuItem>
                  <MenuItem value="qna">Q&A</MenuItem>
                  <MenuItem value="tip">팁</MenuItem>
                  <MenuItem value="study">스터디</MenuItem>
                </Select>
              </FormControl>
              {isAdmin && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                  글쓰기
                </Button>
              )}
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Notices Section */}
                {!filterType && notices.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>📢 공지사항</Typography>
                    {notices.map(post => (
                      <Card key={post.id} sx={{ mb: 1, cursor: 'pointer', '&:hover': { bgcolor: '#fafafa' } }} onClick={() => handlePostClick(post)}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="공지" color="error" size="small" />
                          {post.isPinned && <PushPinIcon fontSize="small" color="action" />}
                          <Typography variant="body1" fontWeight={500} sx={{ flex: 1 }}>{post.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(post.createdAt).toLocaleDateString('ko-KR')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <VisibilityIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">{post.viewCount}</Typography>
                          </Box>
                          {isAdmin && (
                            <Box>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(post); }}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(post.id); }}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}

                {/* Other Posts Grid */}
                <Grid container spacing={2}>
                  {(filterType ? posts : otherPosts).map(post => (
                    <Grid item xs={12} sm={6} md={4} key={post.id}>
                      <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 4 } }} onClick={() => handlePostClick(post)}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip label={TYPE_LABELS[post.type]} color={TYPE_COLORS[post.type]} size="small" />
                            {post.isPinned && <PushPinIcon fontSize="small" color="action" />}
                          </Box>
                          <Typography variant="subtitle1" fontWeight={600} noWrap>{post.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {post.content}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, color: 'text.secondary' }}>
                            <Typography variant="caption">{post.authorName}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption">{new Date(post.createdAt).toLocaleDateString('ko-KR')}</Typography>
                              <VisibilityIcon sx={{ fontSize: 12 }} />
                              <Typography variant="caption">{post.viewCount}</Typography>
                            </Box>
                          </Box>
                          {isAdmin && (
                            <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(post); }}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(post.id); }}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {posts.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography color="text.secondary">게시글이 없습니다.</Typography>
                  </Box>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Container>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? '게시글 수정' : '새 게시글'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>유형</InputLabel>
              <Select value={editForm.type} label="유형" onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}>
                <MenuItem value="notice">공지</MenuItem>
                <MenuItem value="free">자유</MenuItem>
                <MenuItem value="qna">Q&A</MenuItem>
                <MenuItem value="tip">팁</MenuItem>
                <MenuItem value="study">스터디</MenuItem>
              </Select>
            </FormControl>
            <TextField label="제목" fullWidth value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
            <TextField label="내용" fullWidth multiline rows={10} value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} />
            <FormControlLabel
              control={<Switch checked={editForm.isPinned} onChange={(e) => setEditForm({ ...editForm, isPinned: e.target.checked })} />}
              label="상단 고정"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSubmit}>저장</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
